import DatePickerField from "components/controls/DatePickerField";
import TextInputField from "components/controls/TextInputField";
import VinOrLicensePlateSearchTextInputField from "components/controls/VinOrLicensePlateTextInputField";
import { SetLicensePlateDialog } from "components/SetLicensePlateDialog";
import { constants } from "library/constants";
import {
  getNoLicensePlateValue,
  hasVehicleBeenActivated,
  isDisabledExternalAndHasBeenActivated,
  isNoLicensePlate,
} from "pages/vehicle/helpers";
import { isClientHiddenVehicleField } from "pages/vehicle/clientVehicleSpec";
import { vehicleFormClasses } from "pages/vehicle/VehicleForm";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "store/hooks";
import { UserRole } from "types/types";

const GeneralFormItems = ({ initialValues, vehicle, route, formikProps }) => {
  const { t } = useTranslation();
  const userRole = useSelector((s) => s.auth.userRole);
  const taxonomy = useSelector((s) => s.taxonomy);
  const [isSetLicensePlateDialogOpen, setIsSetLicensePlateDialogOpen] =
    useState(false);

  const { values, errors, handleChange, setFieldValue, dirty } = formikProps;
  const { createVehicle, duplicateVehicle, createTradeVehicle, editVehicle } =
    constants;
  const vehicleAppSettings = useSelector((s) => s.vehicle.vehicleAppSettings);

  // Helper function to check if field should be visible
  const isFieldVisible = (fieldName: string) => {
    if (
      isClientHiddenVehicleField(
        fieldName,
        vehicleAppSettings?.COUNTRY_CODE,
        userRole
      )
    ) {
      return false;
    }
    return vehicleAppSettings?.VISIBLE_FIELDS?.includes(fieldName) ?? true;
  };

  const vehicleLastStatusDateVisible = false;
  // route === createVehicle ||
  // route === duplicateVehicle ||
  // route === createTradeVehicle;

  const isTradeLicensePlate =
    route === createTradeVehicle || vehicle?.isTradeLicensePlate;
  const confidentiality = useSelector(
    (s) => s.vehicle?.scannedFormFields?.confidentialityInfo
  );
  const isLicensePlateDisabled = ({ vehicle, route }: any) => {
    return (
      hasVehicleBeenActivated({ vehicle, route }) || route === "readOnlyVehicle"
    );
  };
  const licensePlateDisabled = isLicensePlateDisabled({ route, vehicle });
  // editable drop-down: the "no license plate" value of the country can be picked or a plate typed
  // (not for trade license plates, which are real plates)
  const licensePlateOptions =
    !licensePlateDisabled && !isTradeLicensePlate
      ? [getNoLicensePlateValue(vehicleAppSettings?.COUNTRY_CODE)]
      : undefined;

  // external users can replace the "no license plate" value (KEINES/NONE) of an activated vehicle;
  // internal users do it through the timeline (correct license plate)
  const canSetLicensePlate =
    route === editVehicle &&
    userRole === UserRole.external &&
    hasVehicleBeenActivated({ vehicle, route }) &&
    taxonomy.VehicleStatusCode.byId[vehicle?.vehicleStatusCode]?.code !==
      "VehicleStatusCode.DEACTIVATED" &&
    !isTradeLicensePlate &&
    isNoLicensePlate(vehicle?.licensePlate);

  return (
    <div className={vehicleFormClasses.container}>
      {/* 1 - License Plate */}
      {isFieldVisible("licensePlate") && (
        <div className={vehicleFormClasses.item}>
          {/* search is offered only while the license plate is editable, otherwise it is replaced by the plain (read-only) field */}
          {vehicleAppSettings?.HAS_SEARCH_BY_LICENSE_PLATE_OR_VIN &&
          !licensePlateDisabled ? (
            <VinOrLicensePlateSearchTextInputField
              value={values.licensePlate}
              errors={errors}
              maxLength={32}
              onChange={handleChange}
              name={"licensePlate"}
              required
              disabled={licensePlateDisabled}
              label={"bfm.vehicleForm.licensePlate.label"}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "licensePlate"
              )}
              confidentiality={confidentiality?.LicensePlate}
              setFieldValue={setFieldValue}
              options={licensePlateOptions}
            />
          ) : (
            <TextInputField
              value={values.licensePlate}
              errors={errors}
              required
              maxLength={12}
              onChange={handleChange}
              name={"licensePlate"}
              disabled={licensePlateDisabled}
              label={"bfm.vehicleForm.licensePlate.label"}
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "licensePlate"
              )}
              confidentiality={confidentiality?.LicensePlate}
              options={licensePlateOptions}
              {...(canSetLicensePlate && {
                actionButton: {
                  iconName: "Edit",
                  // saving the license plate reloads the vehicle form, so unsaved changes must be saved first
                  title: dirty
                    ? t("bfm.setLicensePlate.saveChangesFirst")
                    : t("bfm.setLicensePlate.label"),
                  onClick: () => setIsSetLicensePlateDialogOpen(true),
                  disabled: dirty,
                },
              })}
            />
          )}
          {isSetLicensePlateDialogOpen && (
            <SetLicensePlateDialog
              close={() => setIsSetLicensePlateDialogOpen(false)}
            />
          )}
        </div>
      )}

      {/* 2 - Vehicle Last Status Date */}
      {isFieldVisible("vehicleLastStatusDate") &&
        vehicleLastStatusDateVisible && (
          <div className={vehicleFormClasses.item}>
            <DatePickerField
              errors={errors}
              setFieldValue={setFieldValue}
              defaultValue={initialValues.vehicleLastStatusDate}
              name={"vehicleLastStatusDate"}
              disabled={route === "readOnlyVehicle"}
              label={"bfm.vehicleForm.vehicleLastStatusDate.label"}
              placeholder={t("bfm.selectDate")}
              required
              isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
                "vehicleLastStatusDate"
              )}
              confidentiality={confidentiality?.VehicleLastStatusDate}
            />
          </div>
        )}

      {/* 3 - Registration Date */}
      {isFieldVisible("registrationDate") && (
        <div className={vehicleFormClasses.item}>
          <DatePickerField
            errors={errors}
            setFieldValue={setFieldValue}
            defaultValue={values.registrationDate}
            required
            name="registrationDate"
            disabled={isDisabledExternalAndHasBeenActivated({
              vehicleAppSettings,
              route,
              vehicle,
              userRole,
            })}
            label={"bfm.vehicleForm.registrationDate.label"}
            placeholder={t("bfm.selectDate")}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "registrationDate"
            )}
            confidentiality={confidentiality?.RegistrationDate}
          />
        </div>
      )}

      {/* 4 - Vehicle Owner */}
      {isFieldVisible("vehicleOwner") && (
        <div className={vehicleFormClasses.item}>
          <TextInputField
            value={values.vehicleOwner}
            errors={errors}
            maxLength={128}
            onChange={handleChange}
            name={"vehicleOwner"}
            disabled={route === "readOnlyVehicle"}
            label={"bfm.vehicleForm.vehicleOwner.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "vehicleOwner"
            )}
            confidentiality={confidentiality?.VehicleOwner}
          />
        </div>
      )}

      {/* 5 - Vehicle Owner Address */}
      {isFieldVisible("vehicleOwnerAddress") && (
        <div className={vehicleFormClasses.item}>
          <TextInputField
            value={values.vehicleOwnerAddress}
            errors={errors}
            maxLength={512}
            onChange={handleChange}
            name={"vehicleOwnerAddress"}
            disabled={route === "readOnlyVehicle"}
            label={"bfm.vehicleForm.vehicleOwnerAddress.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "vehicleOwnerAddress"
            )}
            confidentiality={confidentiality?.VehicleOwnerAddress}
          />
        </div>
      )}

      {/* 6 - Vehicle Operator */}
      {isFieldVisible("vehicleOperator") && (
        <div className={vehicleFormClasses.item}>
          <TextInputField
            value={values.vehicleOperator}
            errors={errors}
            maxLength={128}
            onChange={handleChange}
            name={"vehicleOperator"}
            disabled={route === "readOnlyVehicle"}
            label={"bfm.vehicleForm.vehicleOperator.label"}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "vehicleOperator"
            )}
          />
        </div>
      )}
    </div>
  );
};

export default GeneralFormItems;
